import type {
  TEntityMaskItem,
  IEntityParentObjectForCreate,
} from '~/types/models'
import { isEntityMaskData } from '~/types/models'

// When EDITING, settle each field's `unrendered` from its own
// `getStateForUnrendered` BEFORE EntityMaskForm runs its value-affecting
// renderUnrender pass.
//
// Why: a field that is statically `unrendered: true` but should be shown for the
// entity being edited (e.g. opportunity-silo `isFinal`, the company LOA fields)
// otherwise registers as a fresh unrendered -> rendered transition on dialog open.
// That transition trips renderUnrender's "field was just revealed -> apply its
// `default`" branch, which overwrites the value just hydrated from the entity with
// the field's create-time default (e.g. a stored `isFinal: true` becomes `false`).
// Running the identical predicate with the same inputs immediately beforehand makes
// startingState === endingState, so no spurious transition fires and the hydrated
// value survives.
//
// No-op on create: the `default + start:undefined` dirty-forcing idiom used by
// hidden-on-create fields still runs through setDefaultValues, untouched.
export function presetUnrenderedStatesForEditing({
  inputFields,
  entityData,
  isEditing,
  entityParentObjectForCreate,
  isSubentityDialog,
}: {
  inputFields: TEntityMaskItem[]
  entityData: object | undefined
  isEditing: boolean
  entityParentObjectForCreate: IEntityParentObjectForCreate | undefined
  isSubentityDialog: boolean
}) {
  if (!isEditing) {
    return
  }
  inputFields.filter(isEntityMaskData).forEach((ifield) => {
    if (ifield.ignoreSetStateRequests) {
      return
    }
    const getStateForUnrendered = ifield.getStateForUnrendered
    if (getStateForUnrendered === undefined) {
      return
    }
    // Keep this 5-arg call identical to the one in EntityMaskForm's
    // setInputFieldsState so the two unrendered evaluations cannot diverge.
    ifield.unrendered = getStateForUnrendered(
      inputFields,
      entityData,
      isEditing,
      entityParentObjectForCreate,
      isSubentityDialog
    )
  })
}
